import type { ContentProperties, MediaStream } from "@ca.vmedia.tv/domains";
import type { StreamRequest, GetItemPropertiesRequest, ExposeInterface } from "./types";
import { Services } from "@ca.vmedia.tv/services";
import { Logger } from '@/utils';
import type { AxiosError } from 'axios';

export const useMedia = defineStore('Media', (): ExposeInterface => {
    const stream: Ref<MediaStream | null> = ref(null);
    const streamProperties: Ref<ReadonlyArray<ContentProperties> | null> = ref(null);
    const streamPropertiesLoading: Ref<boolean> = ref(false);

    const getCurrentStream = computed((): MediaStream | null => stream.value);
    const getStreamPosition = computed((): number => {
        if (streamProperties.value) {
            const streamProperty = streamProperties.value;
            return streamProperty[0].streamPosition!;
        } else {
            return 0;
        }
    });
    const getStreamFavorite = computed((): boolean => {
        if (streamProperties.value) {
            const streamProperty = streamProperties.value;
            return streamProperty[0].isFavourite!;
        } else {
            return false;
        }
    });
    const getStreamPropertiesLoading = computed((): boolean => streamPropertiesLoading.value);

    const fetchStream = async (request: StreamRequest): Promise<MediaStream | null> => {
        // Do not wrap this in try catch, as it will be handled in the useFetchStream composable
        let fetchedStream = null;
        if (request.isRestart) {
            fetchedStream = await Services.Instance.domains.mediaRepository.getRestartedStream(request.id, request.type);
        } else {
            fetchedStream = await Services.Instance.domains.mediaRepository.getStream(request.id, request.type);
        }
        stream.value = fetchedStream;
        return fetchedStream;
    };

    const fetchItemProperties = async (request: GetItemPropertiesRequest): Promise<ReadonlyArray<ContentProperties>> => {
        try {
            if (!request.type) {
                return [];
            }
            streamPropertiesLoading.value = true;
            const properties = await Services.Instance.domains.mediaRepository.getItemProperties(request.ids, request.type, request.props);
            streamProperties.value = properties;
            streamPropertiesLoading.value = false;
            return properties;
        } catch (error) {
            Logger.captureError(error as AxiosError);
            streamPropertiesLoading.value = false;
            return [];
        }
    };


    const clearAll = (): void => {
        stream.value = null;
        streamProperties.value = null;
        streamPropertiesLoading.value = false;
    };

    return {
        getCurrentStream,
        getStreamPosition,
        getStreamFavorite,
        getStreamPropertiesLoading,
        fetchStream,
        fetchItemProperties,
        clearAll,
    };
});